import * as React from 'react';
import type {
  ButtonHTMLAttributes,
  ForwardedRef,
  ReactNode,
  RefObject,
} from 'react';
import {
  MessageScroller,
  useMessageScroller,
} from '@shadcn/react/message-scroller';
import type {ChatMessage} from '../hooks/chat/types';
import {usePrevious} from '../hooks/use-previous';
import {Icons} from '../icons';
import {isSyntheticMessageId} from '../lib/streaming';
import {cn} from '../lib/utils';
import {
  Conversation,
  ConversationContent,
  ConversationEmptyState,
} from './conversation';
import {Button} from '../primitives';
import {Message, MessageContent, MessageResponse} from './message';
import {
  Suggestions,
  SuggestionsLabel,
  SuggestionsList,
  Suggestion,
} from './suggestion';
import {useChatSurfaceVariant, type ChatSurfaceVariant} from './chat-variant';
import {Thinking, ThinkingDots, ThinkingLabel} from './thinking';
import {
  ToolCallActivity,
  ToolCallStack,
  type ToolCallConfigMap,
} from './tool-call-stack';
import {useTurnActivity} from './use-turn-activity';

export interface ChatConversationHandle {
  scrollToBottom: (behavior?: ScrollBehavior) => void;
  reset: () => void;
}

export interface ChatConversationProps {
  messages: ChatMessage[];
  isAwaitingResponse?: boolean;
  isStreaming?: boolean;
  error?: string | null;
  /** Sends the failed message again; shown as a Retry action beside the error. */
  onRetry?: () => void;

  renderMessage?: (message: ChatMessage) => ReactNode;

  suggestions?: string[];
  onSuggestionSelect?: (suggestion: string) => void;
  emptyState?: ReactNode;

  toolCallConfig?: ToolCallConfigMap;

  /** Ref that receives the scroll viewport element (e.g. for ScrollToBottomButton). */
  scrollRef?: RefObject<HTMLDivElement | null>;
  showScrollButton?: boolean;
  scrollButtonClassName?: string;
  /**
   * Agent only: follow the reply to the bottom as the default surface does,
   * instead of pinning the question. For viewports too short to pin in.
   */
  followReply?: boolean;

  className?: string;
  contentClassName?: string;
}

const SCROLL_EDGE_THRESHOLD = 100;
/** A new question lands this far below the top, with the reply arriving under it. */
const QUESTION_TOP_OFFSET = 24;

export const ChatConversation = React.forwardRef<
  ChatConversationHandle,
  ChatConversationProps
>((props, ref) => {
  const variant = useChatSurfaceVariant();
  const pinsQuestion = variant === 'agent' && !props.followReply;
  return (
    // Following the bottom and holding a question in place are alternatives
    // in the scroller: the first content change after a pin (the activity row)
    // would flip it into following and drop the question to the bottom.
    <MessageScroller.Provider
      autoScroll={!pinsQuestion}
      defaultScrollPosition={pinsQuestion ? 'last-anchor' : 'end'}
      scrollEdgeThreshold={SCROLL_EDGE_THRESHOLD}
      scrollMargin={pinsQuestion ? QUESTION_TOP_OFFSET : undefined}
      // The scroller otherwise keeps a strip of the previous answer above the question.
      scrollPreviousItemPeek={pinsQuestion ? 0 : undefined}
    >
      <ChatConversationInner
        {...props}
        variant={variant}
        pinsQuestion={pinsQuestion}
        handleRef={ref}
      />
    </MessageScroller.Provider>
  );
});
ChatConversation.displayName = 'ChatConversation';

interface ChatConversationInnerProps extends ChatConversationProps {
  variant: ChatSurfaceVariant;
  pinsQuestion: boolean;
  handleRef: ForwardedRef<ChatConversationHandle>;
}

interface MessageKeys {
  messages: ChatMessage[];
  turn: number;
  aliases: ReadonlyMap<string, string>;
}

const liveKey = (turn: number) => `live-${turn}`;

/** A remount when the server copy replaces the placeholder would restart the reply's reveal mid-way. */
function useMessageKeys(messages: ChatMessage[], enabled: boolean) {
  const [keys, setKeys] = React.useState<MessageKeys>(() => ({
    messages,
    turn: 0,
    aliases: new Map(),
  }));

  let current = keys;
  if (enabled && keys.messages !== messages) {
    const previous = keys.messages.at(-1);
    const last = messages.at(-1);
    // A stopped reply settles under a client id first, then the transcript swaps in the server copy.
    const placeholderKey = previous?.streaming
      ? liveKey(keys.turn)
      : previous && isSyntheticMessageId(previous.id)
        ? (keys.aliases.get(previous.id) ?? previous.id)
        : undefined;
    const replacesPlaceholder =
      placeholderKey !== undefined &&
      last?.role === 'assistant' &&
      !last.streaming &&
      !keys.messages.some((message) => message.id === last.id);
    current = {
      messages,
      turn: last?.streaming && !previous?.streaming ? keys.turn + 1 : keys.turn,
      aliases:
        replacesPlaceholder && last
          ? new Map(keys.aliases).set(last.id, placeholderKey)
          : keys.aliases,
    };
    setKeys(current);
  }

  const {turn, aliases} = current;
  return React.useCallback(
    (message: ChatMessage) =>
      message.streaming
        ? liveKey(turn)
        : (aliases.get(message.id) ?? message.id),
    [turn, aliases],
  );
}

function getMessagePresentation(message: ChatMessage) {
  const hasTextContent = Boolean(
    message.content && message.content.trim().length > 0,
  );
  const toolCalls = message.toolCalls ?? [];
  const hasToolCalls = toolCalls.length > 0;
  // Truncated text is self-evidently stopped; interrupted tool work is not.
  const showStoppedIndicator = Boolean(
    message.cancelled && (!hasTextContent || hasToolCalls),
  );
  const isRenderable =
    hasTextContent ||
    hasToolCalls ||
    showStoppedIndicator ||
    message.role !== 'assistant';

  return {
    hasTextContent,
    toolCalls,
    hasToolCalls,
    showStoppedIndicator,
    isRenderable,
  };
}

function ResponseStopped() {
  return (
    <div className="flex items-center gap-2 text-sm text-muted-foreground">
      <Icons.stop className="size-3 fill-current" />
      <span>Response stopped</span>
    </div>
  );
}

function ChatScrollToLatest({className}: {className?: string}) {
  return (
    <MessageScroller.Button
      direction="end"
      behavior="smooth"
      render={(props, {active}) => (
        <button
          {...(props as ButtonHTMLAttributes<HTMLButtonElement>)}
          aria-label="Scroll to bottom"
          className={cn(
            'absolute bottom-4 left-1/2 z-50 flex size-8 -translate-x-1/2 items-center justify-center',
            'rounded-full border border-border bg-background shadow-sm hover:bg-accent',
            'transition-all duration-150 ease-out',
            active
              ? 'translate-y-0 opacity-100'
              : 'pointer-events-none translate-y-2 opacity-0',
            className,
          )}
        >
          <Icons.arrowDown className="size-4 text-foreground" />
        </button>
      )}
    />
  );
}

function ChatConversationInner({
  messages,
  isAwaitingResponse = false,
  isStreaming,
  error,
  onRetry,
  renderMessage,
  suggestions,
  onSuggestionSelect,
  emptyState,
  toolCallConfig,
  variant,
  pinsQuestion,
  scrollRef,
  showScrollButton = false,
  scrollButtonClassName,
  className,
  contentClassName,
  handleRef,
}: ChatConversationInnerProps) {
  const {scrollToEnd} = useMessageScroller();

  // The scroller moves a pinned question into place with `behavior: "auto"`,
  // so the viewport's scroll-behavior decides whether that is a jump or a
  // glide. Smooth only once mounted: opening a chat lands on its position.
  const [hasMounted, setHasMounted] = React.useState(false);
  React.useEffect(() => {
    setHasMounted(true);
  }, []);

  const hasMessages = messages.length > 0;
  const {turnIsLive, showActivityRow, activityLabel, liveToolCalls} =
    useTurnActivity({
      messages,
      isAwaitingResponse,
      isStreaming,
      toolCallConfig,
      variant,
    });

  const messageKey = useMessageKeys(messages, variant === 'agent');

  const lastUserMessageId = React.useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'user') {
        return messages[i].id;
      }
    }

    return undefined;
  }, [messages]);

  React.useImperativeHandle(
    handleRef,
    () => ({
      scrollToBottom: (behavior: ScrollBehavior = 'smooth') => {
        scrollToEnd({behavior});
      },
      reset: () => {
        scrollToEnd({behavior: 'instant'});
      },
    }),
    [scrollToEnd],
  );

  // Force scroll when a new user message is sent. A pinned question is a
  // scroll anchor instead and the scroller places it itself.
  const prevLastUserMessageId = usePrevious(lastUserMessageId);
  React.useEffect(() => {
    if (
      !pinsQuestion &&
      lastUserMessageId &&
      prevLastUserMessageId !== undefined &&
      lastUserMessageId !== prevLastUserMessageId
    ) {
      scrollToEnd({behavior: 'smooth'});
    }
  }, [lastUserMessageId, prevLastUserMessageId, scrollToEnd, pinsQuestion]);

  const handleSuggestionClick = React.useCallback(
    (suggestion: string) => {
      scrollToEnd({behavior: 'instant'});
      onSuggestionSelect?.(suggestion);
    },
    [onSuggestionSelect, scrollToEnd],
  );

  const defaultRenderMessage = React.useCallback(
    (message: ChatMessage) => (
      <Message from={message.role}>
        <MessageContent>
          {message.role === 'error' ? (
            message.content
          ) : (
            <MessageResponse>
              <p>{message.content}</p>
            </MessageResponse>
          )}
        </MessageContent>
      </Message>
    ),
    [],
  );

  const messageRenderer = renderMessage ?? defaultRenderMessage;

  const renderChatMessage = React.useCallback(
    (message: ChatMessage) => {
      const presentation = getMessagePresentation(message);
      if (!presentation.isRenderable) {
        return null;
      }

      const {hasTextContent, toolCalls, hasToolCalls, showStoppedIndicator} =
        presentation;
      const rendersToolStack = hasToolCalls && variant !== 'agent';
      // On the agent surface the settled message owns the record of its tool
      // calls; while it streams, the activity row does.
      const rendersSettledSteps =
        hasToolCalls && variant === 'agent' && !message.streaming;
      if (
        !hasTextContent &&
        !rendersToolStack &&
        !rendersSettledSteps &&
        !showStoppedIndicator
      ) {
        return null;
      }

      return (
        <MessageScroller.Item
          key={variant === 'agent' ? messageKey(message) : message.id}
          messageId={message.id}
          scrollAnchor={pinsQuestion && message.id === lastUserMessageId}
          className="grid gap-4"
        >
          {variant === 'agent' ? (
            <>
              {(hasTextContent || rendersSettledSteps) &&
                messageRenderer(message)}
              {showStoppedIndicator && <ResponseStopped />}
            </>
          ) : (
            <>
              {hasTextContent && messageRenderer(message)}
              {(rendersToolStack || showStoppedIndicator) && (
                <div className="grid gap-2">
                  {rendersToolStack && (
                    <ToolCallStack
                      toolCalls={toolCalls}
                      toolConfig={toolCallConfig}
                    />
                  )}
                  {showStoppedIndicator && <ResponseStopped />}
                </div>
              )}
            </>
          )}
        </MessageScroller.Item>
      );
    },
    [
      lastUserMessageId,
      messageKey,
      messageRenderer,
      pinsQuestion,
      toolCallConfig,
      variant,
    ],
  );

  const conversation = (
    <Conversation
      ref={scrollRef}
      className={cn(pinsQuestion && hasMounted && 'scroll-smooth', className)}
    >
      {hasMessages || showActivityRow || error ? (
        <ConversationContent className={contentClassName}>
          {messages.map(renderChatMessage)}

          {variant === 'agent' ? (
            <ToolCallActivity
              toolCalls={liveToolCalls}
              toolConfig={toolCallConfig}
              pendingLabel={showActivityRow ? activityLabel : undefined}
            />
          ) : (
            showActivityRow && (
              <Thinking>
                <ThinkingDots />
                <ThinkingLabel />
              </Thinking>
            )
          )}

          {error && (
            <Message from="error">
              <MessageContent className="flex-row items-center gap-3">
                <span>{error}</span>
                {/* A turn still running (an unconfirmed Stop) refuses new sends. */}
                {onRetry && !turnIsLive && (
                  <Button
                    variant="link"
                    onClick={onRetry}
                    className="h-auto shrink-0 p-0 text-foreground"
                  >
                    Retry
                  </Button>
                )}
              </MessageContent>
            </Message>
          )}
        </ConversationContent>
      ) : (
        (emptyState ?? (
          <ConversationEmptyState className={contentClassName}>
            {suggestions && suggestions.length > 0 && (
              <Suggestions>
                <SuggestionsLabel />
                <SuggestionsList>
                  {suggestions.map((suggestion, index) => (
                    <Suggestion
                      key={index}
                      suggestion={suggestion}
                      onClick={handleSuggestionClick}
                    />
                  ))}
                </SuggestionsList>
              </Suggestions>
            )}
          </ConversationEmptyState>
        ))
      )}
      {/* Stays mounted: screen readers skip a live region inserted together
          with its first text. Kept out of the message list, where a trailing
          node hides each new question from the scroller's pin. */}
      {variant === 'agent' && (
        <span className="sr-only" aria-live="polite">
          {showActivityRow ? activityLabel : undefined}
        </span>
      )}
    </Conversation>
  );

  if (!showScrollButton) {
    return conversation;
  }

  return (
    <div className="relative flex min-h-0 w-full flex-1 flex-col">
      {conversation}
      <ChatScrollToLatest className={scrollButtonClassName} />
    </div>
  );
}
